import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Radio,
  Result,
  Select,
  Slider,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  BgColorsOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  SwapOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import axios from "axios";
import * as XLSX from "xlsx";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiUrl } from "../config";
import TableContext from "../Context/TablePosition";
import useTableBodyHeight from "../utils/useTableBodyHeight";

/**
 * Özet Sayfası
 *
 * Plastik fabrikasındaki "Özet" ekranının boyahane karşılığı. Plastikte
 * satırlar dönem × ürün iken burada satırlar **asma sırası × malzeme**'dir:
 * son yayınlanan planın (getBSHStartStopResults) her satırı gövde ve
 * gövde ön için ayrı birer kayıt üretir. Her kayda stok yeterliliği
 * uç noktasından (getStockSufficiency) mevcut boyalı stok, aktif/servis
 * bilgisi ve yeterlilik durumu eklenir.
 *
 * Boyahaneye özgü sütunlar: asma/toplama zamanı, renk, gövde tipi,
 * gerçekleşen toplama, yeterlilik ve gövde/renk değişimi işaretleri.
 * Planlı duruş satırları (production_occurs == 0) tek kayıt olarak,
 * "Duruş" tipiyle listelenir ve istenirse gizlenebilir.
 */

const pad = (n) => String(n).padStart(2, "0");

/** "YYYY-MM-DD" + "HH:MM:SS" → Date (yerel saat) */
const toDate = (date, time) => {
  if (!date) return null;
  const d = new Date(`${date}T${time || "00:00:00"}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDateTime = (d) =>
  d
    ? `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
        d.getHours()
      )}:${pad(d.getMinutes())}`
    : "-";

const fmtShort = (d) =>
  d
    ? `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(
        d.getMinutes()
      )}`
    : "";

const fmtIso = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : fmtDateTime(d);
};

/** 8001319792.0 → "8001319792" */
const matCode = (v) => {
  if (v == null || v === "" || Number(v) === 0) return "";
  return String(Math.trunc(Number(v)));
};

const num = (v) => Number(v) || 0;

const TYPE = { BODY: "GOVDE", FRONT: "GOVDE_ON", STOP: "DURUS" };

export default function SummaryPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { setCurrentUrl } = useContext(TableContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState([]);
  const [sufficiency, setSufficiency] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [useBeta, setUseBeta] = useState(false);

  // Filtreler
  const [searchTerm, setSearchTerm] = useState("");
  const [colorFilter, setColorFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState("ALL"); // ALL | GOVDE | GOVDE_ON
  const [serviceFilter, setServiceFilter] = useState("ALL"); // ALL | ACTIVE | SERVICE
  const [showStops, setShowStops] = useState(true);
  const [range, setRange] = useState([0, 100]);

  const username = process.env.REACT_APP_USERNAME;
  const password = process.env.REACT_APP_SECRET_KEY;

  useEffect(() => {
    localStorage.setItem("urlpaintshop", location.pathname);
    if (setCurrentUrl) setCurrentUrl(location.pathname);
  }, [location.pathname, setCurrentUrl]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const prefix = useBeta ? "/beta" : "";
      const auth = { username, password };
      const [planRes, suffRes] = await Promise.all([
        axios.get(`${apiUrl}${prefix}/getBSHStartStopResults`, { auth }),
        axios
          .get(`${apiUrl}${prefix}/getStockSufficiency`, { auth })
          .catch(() => ({ data: null })),
      ]);
      setPlan(Array.isArray(planRes.data) ? planRes.data : []);
      setSufficiency(suffRes.data || null);
      setRange([0, 100]);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Özet verisi çekilemedi:", err);
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err.message ||
          t("summary.loadError")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBeta]);

  /** Malzeme kodu → yeterlilik kalemi */
  const suffByCode = useMemo(() => {
    const map = new Map();
    (sufficiency?.items || []).forEach((it) =>
      map.set(String(it.product_code), it)
    );
    return map;
  }, [sufficiency]);

  /** Plan satırlarını malzeme bazlı kayıtlara açar; kümülatif üretimi hesaplar. */
  const records = useMemo(() => {
    const sorted = [...plan].sort(
      (a, b) =>
        num(a.sequence) - num(b.sequence) ||
        (toDate(a.date, a.period)?.getTime() || 0) -
          (toDate(b.date, b.period)?.getTime() || 0)
    );
    const out = [];

    sorted.forEach((row) => {
      const start = toDate(row.date, row.period);
      const collect = toDate(row.date, row.toplama_period);
      // Toplama asma saatinden küçükse gece yarısını geçmiştir.
      if (start && collect && collect < start)
        collect.setDate(collect.getDate() + 1);

      const base = {
        rowId: row.id,
        sequence: num(row.sequence),
        orderNr: row.order_nr,
        start,
        collect,
        color: row.renk || "",
        description: row.siparis || "",
        flags: {
          colorChange: Boolean(row.is_color_change),
          bodyChange: Boolean(row.is_govde_change),
          frontChange: Boolean(row.is_govde_on_change),
          failure: Boolean(row.is_failure),
          firstStop: Boolean(row.is_first_stop_occured),
          rework: Boolean(row.rework),
          externalAdded: Boolean(row.is_external_added),
          manualDouble: Boolean(row.is_manuel_double),
        },
        asmaStarted: Boolean(row.is_started_asma),
        asmaDone: Boolean(row.is_completed_asma),
        actualAsma: num(row.actual_production_asma),
      };

      const isStop = num(row.production_occurs) === 0;
      if (isStop) {
        out.push({
          ...base,
          key: `${row.id}-0`,
          item: 0,
          type: TYPE.STOP,
          material: "",
          materialName: row.siparis || t("summary.type.stop"),
          bodyType: "",
          isService: null,
          currentStock: null,
          planned: 0,
          actualCollect: null,
          rework: num(row.rework_uretim),
          junk: 0,
          reworkSummary: row.rework_summary || "",
          suff: null,
        });
        return;
      }

      const items = [
        {
          item: 1,
          type: TYPE.BODY,
          material: matCode(row.govde),
          planned: num(row.govde_uretim),
          actualCollect: num(row.actual_production_toplama_govde),
          rework:
            num(row.static_rework_amount_toplama_govde) +
            num(row.dynamic_rework_amount_toplama_govde),
          junk: num(row.junk_amount_toplama_govde),
        },
        {
          item: 2,
          type: TYPE.FRONT,
          material: matCode(row.govde_on),
          planned: num(row.govde_on_uretim),
          actualCollect: num(row.actual_production_toplama_govde_on),
          rework:
            num(row.static_rework_amount_toplama_govde_on) +
            num(row.dynamic_rework_amount_toplama_govde_on),
          junk: num(row.junk_amount_toplama_govde_on),
        },
      ];

      items.forEach((it) => {
        if (!it.material || it.planned <= 0) return;
        const suff = suffByCode.get(it.material) || null;
        out.push({
          ...base,
          ...it,
          key: `${row.id}-${it.item}`,
          materialName: suff?.tanim || row.siparis || "",
          bodyType: suff?.body_type || "",
          isService: suff ? Boolean(suff.is_service) : null,
          currentStock: suff ? num(suff.current_stock) : null,
          reworkSummary: "",
          suff,
        });
      });
    });

    return out;
  }, [plan, suffByCode, t]);

  const timeBounds = useMemo(() => {
    const times = records.map((r) => r.start?.getTime()).filter(Boolean);
    if (!times.length) return null;
    return { min: Math.min(...times), max: Math.max(...times) };
  }, [records]);

  const percentToDate = (p) => {
    if (!timeBounds) return null;
    return new Date(
      timeBounds.min + ((timeBounds.max - timeBounds.min) * p) / 100
    );
  };

  const uniqueColors = useMemo(
    () => [...new Set(records.map((r) => r.color).filter(Boolean))].sort(),
    [records]
  );
  const uniqueMaterials = useMemo(
    () => [...new Set(records.map((r) => r.material).filter(Boolean))].sort(),
    [records]
  );

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const from = percentToDate(range[0]);
    const to = percentToDate(range[1]);
    return records.filter((r) => {
      if (r.type === TYPE.STOP && !showStops) return false;
      if (typeFilter === "GOVDE" && r.type !== TYPE.BODY) return false;
      if (typeFilter === "GOVDE_ON" && r.type !== TYPE.FRONT) return false;
      if (serviceFilter === "ACTIVE" && r.isService === true) return false;
      if (serviceFilter === "SERVICE" && r.isService !== true) return false;
      if (colorFilter.length && !colorFilter.includes(r.color)) return false;
      if (from && to && r.start && (r.start < from || r.start > to))
        return false;
      if (term) {
        const hay = [
          r.material,
          r.materialName,
          r.orderNr,
          r.color,
          r.bodyType,
          r.description,
          fmtDateTime(r.start),
        ]
          .map((v) => String(v ?? "").toLowerCase())
          .join(" ");
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    records,
    searchTerm,
    colorFilter,
    typeFilter,
    serviceFilter,
    showStops,
    range,
    timeBounds,
  ]);

  const totals = useMemo(() => {
    const prod = filteredData.filter((r) => r.type !== TYPE.STOP);
    const sum = (arr, f) => arr.reduce((s, r) => s + f(r), 0);
    return {
      planned: sum(prod, (r) => r.planned),
      plannedBody: sum(
        prod.filter((r) => r.type === TYPE.BODY),
        (r) => r.planned
      ),
      plannedFront: sum(
        prod.filter((r) => r.type === TYPE.FRONT),
        (r) => r.planned
      ),
      actualCollect: sum(prod, (r) => r.actualCollect || 0),
      stops: filteredData.filter((r) => r.type === TYPE.STOP).length,
      colorChanges: new Set(
        filteredData.filter((r) => r.flags.colorChange).map((r) => r.rowId)
      ).size,
      bodyChanges: new Set(
        filteredData
          .filter((r) => r.flags.bodyChange || r.flags.frontChange)
          .map((r) => r.rowId)
      ).size,
      criticalMaterials: new Set(
        filteredData
          .filter((r) => r.suff?.status === "Kritik")
          .map((r) => r.material)
      ).size,
    };
  }, [filteredData]);

  const typeLabel = (type) =>
    type === TYPE.BODY
      ? t("summary.type.body")
      : type === TYPE.FRONT
      ? t("summary.type.frontBody")
      : t("summary.type.stop");

  const serviceLabel = (r) =>
    r.type === TYPE.STOP
      ? "-"
      : r.isService == null
      ? "-"
      : r.isService
      ? t("summary.service.service")
      : t("summary.service.active");

  const sufficiencyText = (r) => {
    const s = r.suff;
    if (!s) return "-";
    if (s.covers_whole_plan) return t("summary.sufficiency.coversAll");
    const m = s.remaining_minutes;
    if (m == null) return fmtIso(s.sufficiency_date);
    if (m <= 0) return t("summary.sufficiency.depleted");
    const h = Math.floor(m / 60);
    const mm = Math.round(m % 60);
    return h > 0 ? `${h} ${t("summary.hourShort")} ${mm} ${t("summary.minShort")}` : `${mm} ${t("summary.minShort")}`;
  };

  const tableRef = useRef(null);
  const tableHeight = useTableBodyHeight(tableRef, 84, [loading, filteredData.length]);

  const handleExportExcel = () => {
    if (!filteredData.length) return;
    const rows = filteredData.map((r) => ({
      [t("summary.columns.sequence")]: r.item ? `${r.sequence}.${r.item}` : r.sequence,
      [t("summary.columns.orderNr")]: r.orderNr,
      [t("summary.columns.start")]: fmtDateTime(r.start),
      [t("summary.columns.collect")]: fmtDateTime(r.collect),
      [t("summary.columns.material")]: r.material,
      [t("summary.columns.type")]: typeLabel(r.type),
      [t("summary.columns.description")]: r.materialName,
      [t("summary.columns.color")]: r.color,
      [t("summary.columns.bodyType")]: r.bodyType,
      [t("summary.columns.service")]: serviceLabel(r),
      [t("summary.columns.currentStock")]: r.currentStock ?? "",
      [t("summary.columns.planned")]: r.planned,
      [t("summary.columns.actualCollect")]: r.actualCollect ?? "",
      [t("summary.columns.sufficiency")]: sufficiencyText(r),
      [t("summary.columns.sufficiencyStatus")]: r.suff?.status || "",
      [t("summary.columns.notes")]: noteList(r).join(", "),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0]).map((k) => ({
      wch: Math.max(10, Math.min(36, k.length + 4)),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("summary.reportTitle"));
    const dateStr = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `Ozet_Raporu_${dateStr}.xlsx`);
  };

  const noteList = (r) => {
    const f = r.flags;
    const n = [];
    if (f.colorChange) n.push(t("summary.notes.colorChange"));
    if (r.type === TYPE.BODY && f.bodyChange) n.push(t("summary.notes.bodyChange"));
    if (r.type === TYPE.FRONT && f.frontChange) n.push(t("summary.notes.frontChange"));
    if (r.type === TYPE.STOP && (f.bodyChange || f.frontChange))
      n.push(t("summary.notes.bodyChange"));
    if (f.failure) n.push(t("summary.notes.failure"));
    if (f.rework) n.push(t("summary.notes.rework"));
    if (f.externalAdded) n.push(t("summary.notes.externalAdded"));
    if (f.manualDouble) n.push(t("summary.notes.manualDouble"));
    if (r.asmaDone) n.push(t("summary.notes.asmaDone"));
    else if (r.asmaStarted) n.push(t("summary.notes.asmaStarted"));
    return n;
  };

  const columns = [
    {
      title: t("summary.columns.sequence"),
      dataIndex: "sequence",
      key: "sequence",
      width: 64,
      align: "center",
      fixed: "left",
      sorter: (a, b) => a.sequence - b.sequence || a.item - b.item,
      defaultSortOrder: "ascend",
      render: (v, r) => (
        <span className="font-mono text-content-muted">
          {v}
          {r.item ? <span className="text-content-faint">.{r.item}</span> : null}
        </span>
      ),
    },
    {
      title: t("summary.columns.start"),
      dataIndex: "start",
      key: "start",
      width: 130,
      sorter: (a, b) => (a.start?.getTime() || 0) - (b.start?.getTime() || 0),
      render: (v) => <span className="font-mono text-content">{fmtDateTime(v)}</span>,
    },
    {
      title: t("summary.columns.collect"),
      dataIndex: "collect",
      key: "collect",
      width: 130,
      render: (v) => <span className="font-mono text-content-muted">{fmtDateTime(v)}</span>,
    },
    {
      title: t("summary.columns.orderNr"),
      dataIndex: "orderNr",
      key: "orderNr",
      width: 90,
      render: (v) => <span className="font-mono text-content">{v || "-"}</span>,
    },
    {
      title: t("summary.columns.material"),
      dataIndex: "material",
      key: "material",
      width: 120,
      filters: uniqueMaterials.map((m) => ({ text: m, value: m })),
      onFilter: (value, r) => r.material === value,
      filterSearch: true,
      render: (v) =>
        v ? <span className="font-mono font-semibold text-content">{v}</span> : "-",
    },
    {
      title: t("summary.columns.type"),
      dataIndex: "type",
      key: "type",
      width: 96,
      render: (v) =>
        v === TYPE.BODY ? (
          <Tag color="blue" className="m-0">{t("summary.type.body")}</Tag>
        ) : v === TYPE.FRONT ? (
          <Tag color="purple" className="m-0">{t("summary.type.frontBody")}</Tag>
        ) : (
          <Tag color="default" className="m-0" icon={<PauseCircleOutlined />}>
            {t("summary.type.stop")}
          </Tag>
        ),
    },
    {
      title: t("summary.columns.description"),
      dataIndex: "materialName",
      key: "materialName",
      width: 220,
      ellipsis: { showTitle: false },
      render: (v, r) => (
        <Tooltip title={r.reworkSummary ? `${v} — ${r.reworkSummary}` : v}>
          <span className={r.type === TYPE.STOP ? "text-content-muted italic" : "text-content"}>
            {v || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: t("summary.columns.color"),
      dataIndex: "color",
      key: "color",
      width: 90,
      render: (v) => (v ? <span className="text-content">{v}</span> : "-"),
    },
    {
      title: t("summary.columns.bodyType"),
      dataIndex: "bodyType",
      key: "bodyType",
      width: 80,
      align: "center",
      render: (v) => (v ? <span className="font-mono text-content">{v}</span> : "-"),
    },
    {
      title: t("summary.columns.service"),
      key: "service",
      width: 100,
      align: "center",
      filters: [
        { text: t("summary.service.active"), value: "ACTIVE" },
        { text: t("summary.service.service"), value: "SERVICE" },
      ],
      onFilter: (value, r) =>
        value === "SERVICE" ? r.isService === true : r.isService === false,
      render: (_, r) =>
        r.type === TYPE.STOP || r.isService == null ? (
          <span className="text-content-faint">-</span>
        ) : r.isService ? (
          <Tag color="gold" className="m-0">{t("summary.service.service")}</Tag>
        ) : (
          <Tag color="green" className="m-0">{t("summary.service.active")}</Tag>
        ),
    },
    {
      title: t("summary.columns.currentStock"),
      dataIndex: "currentStock",
      key: "currentStock",
      width: 100,
      align: "right",
      sorter: (a, b) => (a.currentStock ?? -1) - (b.currentStock ?? -1),
      render: (v) =>
        v == null ? (
          <span className="text-content-faint">-</span>
        ) : (
          <span className="font-semibold text-content">{v.toLocaleString("tr-TR")}</span>
        ),
    },
    {
      title: t("summary.columns.planned"),
      dataIndex: "planned",
      key: "planned",
      width: 100,
      align: "right",
      sorter: (a, b) => a.planned - b.planned,
      render: (v, r) =>
        r.type === TYPE.STOP ? (
          <span className="text-content-faint">-</span>
        ) : (
          <span className="font-bold text-state-info">{v.toLocaleString("tr-TR")}</span>
        ),
    },
    {
      title: t("summary.columns.actualCollect"),
      dataIndex: "actualCollect",
      key: "actualCollect",
      width: 110,
      align: "right",
      sorter: (a, b) => (a.actualCollect || 0) - (b.actualCollect || 0),
      render: (v, r) =>
        r.type === TYPE.STOP || !v ? (
          <span className="text-content-faint">{r.type === TYPE.STOP ? "-" : 0}</span>
        ) : (
          <span className="font-semibold text-state-ok">{v.toLocaleString("tr-TR")}</span>
        ),
    },
    {
      title: t("summary.columns.sufficiency"),
      key: "sufficiency",
      width: 150,
      sorter: (a, b) =>
        (a.suff?.remaining_minutes ?? 1e9) - (b.suff?.remaining_minutes ?? 1e9),
      render: (_, r) => {
        const s = r.suff;
        if (!s) return <span className="text-content-faint">-</span>;
        if (s.covers_whole_plan)
          return (
            <Tag color="success" className="m-0" icon={<CheckCircleOutlined />}>
              {t("summary.sufficiency.coversAll")}
            </Tag>
          );
        const critical = s.status === "Kritik";
        return (
          <Tooltip title={fmtIso(s.sufficiency_date)}>
            <Tag
              color={critical ? "error" : "processing"}
              className="m-0"
              icon={critical ? <WarningOutlined /> : <ClockCircleOutlined />}
            >
              {sufficiencyText(r)}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t("summary.columns.notes"),
      key: "notes",
      width: 200,
      render: (_, r) => {
        const notes = noteList(r);
        if (!notes.length) return <span className="text-content-faint">-</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {notes.map((n) => (
              <Tag key={n} className="m-0 text-2xs" bordered={false}>
                {n}
              </Tag>
            ))}
          </div>
        );
      },
    },
  ];

  const batch = plan[0]?.order_batch;

  return (
    <div className="w-full h-full p-3 laptop:p-4">
      {/* Üst Başlık & Eylemler */}
      <div className="mb-3 flex flex-col gap-2 laptop:flex-row laptop:items-center laptop:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-lg font-bold tracking-tight text-content laptop:text-xl">
              {t("summary.title")}
            </h1>
            {batch?.id && (
              <span className="rounded-full bg-brand-500/15 px-3 py-1 text-xs font-semibold text-brand-600 dark:text-brand-300 border border-brand-500/30">
                {t("summary.plan")} #{batch.id} · {fmtIso(batch.batch_date)}
              </span>
            )}
            {sufficiency?.legacy_plan && (
              <Tag color="warning" className="m-0">{t("summary.legacyPlan")}</Tag>
            )}
          </div>
          <p className="mt-0.5 max-w-3xl truncate text-xs text-content-muted">
            {t("summary.subtitle")}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {lastUpdated && (
            <span className="hidden sm:flex items-center gap-1 text-xs text-content-muted mr-1">
              <ClockCircleOutlined /> {lastUpdated.toLocaleTimeString("tr-TR")}
            </span>
          )}
          <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchData}>
            {t("summary.refresh")}
          </Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            disabled={filteredData.length === 0}
          >
            {t("summary.exportExcel")}
          </Button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          message={t("summary.loadErrorTitle")}
          description={error}
          action={
            <Button size="small" type="primary" danger onClick={fetchData}>
              {t("summary.retry")}
            </Button>
          }
          className="mb-3"
        />
      )}

      {/* Metrik Kartları */}
      <div className="mb-3 grid grid-cols-2 laptop:grid-cols-4 gap-3">
        <div className="rounded-xl border border-line bg-surface px-3 py-2 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-content-muted">
              {t("summary.metrics.planned")}
            </span>
            <div className="flex h-6 w-6 items-center justify-center rounded-md text-xs bg-surface-sunken text-content-muted">
              <DatabaseOutlined />
            </div>
          </div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="text-xl font-extrabold text-content">
              {totals.planned.toLocaleString("tr-TR")}
            </span>
            <span className="text-xs text-content-muted">
              {t("summary.metrics.plannedSplit", {
                body: totals.plannedBody.toLocaleString("tr-TR"),
                front: totals.plannedFront.toLocaleString("tr-TR"),
              })}
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-line bg-surface px-3 py-2 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-state-ok">
              {t("summary.metrics.actual")}
            </span>
            <div className="flex h-6 w-6 items-center justify-center rounded-md text-xs bg-emerald-500/20 text-state-ok">
              <CheckCircleOutlined />
            </div>
          </div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="text-xl font-extrabold text-state-ok">
              {totals.actualCollect.toLocaleString("tr-TR")}
            </span>
            <span className="text-xs text-content-muted">
              {t("summary.metrics.actualRatio", {
                ratio: totals.planned
                  ? Math.round((totals.actualCollect / totals.planned) * 100)
                  : 0,
              })}
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-line bg-surface px-3 py-2 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-state-warn">
              {t("summary.metrics.stops")}
            </span>
            <div className="flex h-6 w-6 items-center justify-center rounded-md text-xs bg-amber-500/20 text-state-warn">
              <PauseCircleOutlined />
            </div>
          </div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="text-xl font-extrabold text-state-warn">{totals.stops}</span>
            <span className="text-xs text-content-muted">
              {t("summary.metrics.changes", {
                color: totals.colorChanges,
                body: totals.bodyChanges,
              })}
            </span>
          </div>
        </div>

        <div
          className={`rounded-xl border px-3 py-2 shadow-card ${
            totals.criticalMaterials > 0
              ? "border-rose-500/50 bg-rose-500/10"
              : "border-line bg-surface"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-state-bad">
              {t("summary.metrics.critical")}
            </span>
            <div className="flex h-6 w-6 items-center justify-center rounded-md text-xs bg-rose-500/20 text-state-bad">
              <WarningOutlined />
            </div>
          </div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="text-xl font-extrabold text-state-bad">
              {totals.criticalMaterials}
            </span>
            <span className="text-xs text-content-muted">
              {sufficiency
                ? t("summary.metrics.criticalHint", {
                    hours: sufficiency.critical_hours ?? 2,
                  })
                : t("summary.metrics.noSufficiency")}
            </span>
          </div>
        </div>
      </div>

      {/* Araç Çubuğu */}
      <div className="mb-3 rounded-xl border border-line bg-surface px-3 py-2 shadow-card">
        <div className="flex flex-col gap-2 laptop:flex-row laptop:items-center laptop:justify-between">
          {/* Zaman aralığı */}
          <div className="w-full laptop:w-[380px] laptop:shrink-0">
            <div className="flex items-center justify-between text-xs text-content-muted mb-1">
              <span className="flex items-center gap-1">
                <ClockCircleOutlined /> {t("summary.timeRange")}
              </span>
              {timeBounds && (
                <span className="font-mono">
                  {fmtShort(percentToDate(range[0]))} – {fmtShort(percentToDate(range[1]))}
                </span>
              )}
            </div>
            <Slider
              range
              min={0}
              max={100}
              step={1}
              value={range}
              onChange={setRange}
              disabled={!timeBounds}
              tooltip={{ formatter: (v) => fmtDateTime(percentToDate(v)) }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Input
              size="small"
              placeholder={t("summary.searchPlaceholder")}
              prefix={<SearchOutlined className="text-content-faint" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              className="w-full sm:w-56"
            />

            <Select
              size="small"
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              placeholder={
                <span className="flex items-center gap-1">
                  <BgColorsOutlined /> {t("summary.colorPlaceholder")}
                </span>
              }
              className="w-40"
              value={colorFilter}
              onChange={setColorFilter}
              options={uniqueColors.map((c) => ({ label: c, value: c }))}
            />

            <Radio.Group
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="ALL">{t("summary.type.all")}</Radio.Button>
              <Radio.Button value="GOVDE">{t("summary.type.body")}</Radio.Button>
              <Radio.Button value="GOVDE_ON">{t("summary.type.frontBody")}</Radio.Button>
            </Radio.Group>

            <Radio.Group
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="ALL">{t("summary.service.all")}</Radio.Button>
              <Radio.Button value="ACTIVE">{t("summary.service.active")}</Radio.Button>
              <Radio.Button value="SERVICE">{t("summary.service.service")}</Radio.Button>
            </Radio.Group>

            <div className="flex items-center gap-2">
              <Switch size="small" checked={showStops} onChange={setShowStops} />
              <span className="text-xs text-content-muted">{t("summary.showStops")}</span>
            </div>

            <div className="flex items-center gap-2">
              <Switch size="small" checked={useBeta} onChange={setUseBeta} />
              <Tooltip title={t("summary.betaInfo")}>
                <span className="text-xs text-content-muted cursor-help">{t("summary.beta")}</span>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* İçerik */}
      {loading ? (
        <div className="flex h-40 flex-col items-center justify-center gap-3 rounded-xl border border-line bg-surface">
          <Spin size="large" />
          <span className="text-sm text-content-muted">{t("summary.loading")}</span>
        </div>
      ) : !plan.length && !error ? (
        <div className="rounded-xl border border-line bg-surface shadow-card">
          <Result
            status="404"
            title={t("summary.noPlanTitle")}
            subTitle={t("summary.noPlanHint")}
            extra={
              <Button type="primary" onClick={fetchData}>
                {t("summary.retry")}
              </Button>
            }
          />
        </div>
      ) : filteredData.length === 0 ? (
        <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-xl border border-line bg-surface text-center">
          <InfoCircleOutlined className="text-3xl text-content-faint" />
          <span className="text-base font-semibold text-content">{t("summary.emptyTitle")}</span>
          <span className="text-xs text-content-muted">{t("summary.emptyHint")}</span>
        </div>
      ) : (
        <div
          ref={tableRef}
          className="rounded-xl border border-line bg-surface overflow-hidden shadow-card"
        >
          <Table
            dataSource={filteredData}
            columns={columns}
            rowKey="key"
            size="small"
            className="summary-table"
            bordered
            sticky
            scroll={{ x: 1700, y: tableHeight }}
            pagination={false}
            rowClassName={(r) =>
              r.type === TYPE.STOP
                ? "bg-surface-sunken"
                : r.asmaDone
                ? "opacity-60"
                : r.flags.colorChange
                ? "bg-amber-500/5"
                : ""
            }
            footer={() => (
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-content-muted">
                <span>
                  {t("summary.footer.shown", {
                    shown: filteredData.length,
                    total: records.length,
                  })}
                </span>
                <span className="flex items-center gap-3">
                  <span>
                    {t("summary.columns.planned")}:{" "}
                    <span className="font-bold text-content">
                      {totals.planned.toLocaleString("tr-TR")}
                    </span>
                  </span>
                  <span>
                    {t("summary.columns.actualCollect")}:{" "}
                    <span className="font-bold text-state-ok">
                      {totals.actualCollect.toLocaleString("tr-TR")}
                    </span>
                  </span>
                  <span className="flex items-center gap-1">
                    <SwapOutlined /> {totals.bodyChanges}
                    <PauseCircleOutlined className="ml-2" /> {totals.stops}
                  </span>
                </span>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}
